import React from "react";
import styled, { css, useTheme } from "styled-components";
import { ClientData, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Flex from "common/Boxes/Flex";
import LineSkeleton from "common/Skeleton/LineSkeleton";
import { LARGE_MOBILE } from "common/constants";
import Text from "common/Typography/Text";
import RowActions, {
  RowActionsType,
} from "features/users-management/RowActions";
import { useExchangesMetadata } from "@shared/utils/hooks/misc";
import AccountStatusTag from "features/configuration/accounts/AccountConfigurationDrawer/AccountStatusTag";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { newLookTreeRow } from "features/users-management/newLookRows";

interface AccountRowProps {
  account: ClientData;
  balance?: number;
  isFirst?: boolean;
  isLast: boolean;
  isPortfolioLoading: boolean;
  activeAccountsInSlotCount?: number;
}

const AccountRow: React.FC<AccountRowProps> = ({
  account,
  balance,
  isFirst,
  isLast,
  isPortfolioLoading,
  activeAccountsInSlotCount,
}) => {
  const { isNewLook } = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.USERS_MANAGEMENT);
  const { exchangeToDisplayName } = useExchangesMetadata();
  const displayName =
    exchangeToDisplayName[account.exchange] || account.exchange;

  const getBalanceValue = () => {
    if (isPortfolioLoading) {
      return (
        <div style={{ width: 60, marginLeft: 6 }}>
          <LineSkeleton height={14} />
        </div>
      );
    }
    const balanceValue =
      balance !== undefined ? `$${balance.toLocaleString()}` : "N/A";
    return (
      <BalanceText style={{ fontSize: "inherit" }}>{balanceValue}</BalanceText>
    );
  };

  // New look: an account under its slot - the name over the exchange and its
  // status, the balance as the figure on the right.
  if (isNewLook)
    return (
      <NewLookAccountRow>
        <span />
        <span />
        <NewLookNames>
          <NewLookName title={account.client_name}>
            {account.client_name}
          </NewLookName>
          <NewLookMeta>
            <span>{displayName}</span>
            <AccountStatusTag
              status={account.client_status}
              size="small"
              hideInstructions={true}
            />
          </NewLookMeta>
        </NewLookNames>
        {isPortfolioLoading ? (
          <Skeleton width={64} height={12} />
        ) : (
          <NewLookFigure>
            {balance !== undefined ? `$${balance.toLocaleString()}` : "—"}
          </NewLookFigure>
        )}
        <RowActions
          type={RowActionsType.ACCOUNT}
          account={account}
          activeAccountsInSlotCount={activeAccountsInSlotCount}
        />
      </NewLookAccountRow>
    );

  return (
    <AccountRowWrapper>
      <HierarchyLines>
        <VerticalLine $isFirst={!!isFirst} $isLast={isLast} />
        <HorizontalLine />
      </HierarchyLines>
      <AccountMainRow>
        <RowLeft>
          <Flex
            $alignItems="center"
            $flexDirection="row"
            $gap="8px"
            style={{ minWidth: 0, flex: 1 }}
          >
            <Flex
              $alignItems="flex-start"
              $flexDirection="column"
              style={{ minWidth: 0, flex: 1 }}
            >
              <Flex
                $alignItems="center"
                $gap="8px"
                style={{ maxWidth: "100%" }}
              >
                <AccountType $ellipsis>{account.client_name}</AccountType>
                <DesktopStatusTagWrapper>
                  <AccountStatusTag
                    status={account.client_status}
                    hideInstructions={true}
                  />
                </DesktopStatusTagWrapper>
              </Flex>
              <Flex
                $alignItems="flex-start"
                $flexDirection="column"
                style={{ maxWidth: "100%", overflow: "hidden" }}
              >
                <AccountExchangeName type="secondary" $ellipsis>
                  {displayName}
                </AccountExchangeName>
                <MobileBalanceValue as="div">
                  <BalanceWrapper>
                    <Text type="secondary" style={{ fontSize: "inherit" }}>
                      {t("balance")}
                    </Text>
                    {getBalanceValue()}
                  </BalanceWrapper>
                </MobileBalanceValue>
              </Flex>
            </Flex>
            <MobileStatusTagWrapper>
              <AccountStatusTag
                status={account.client_status}
                size="small"
                hideInstructions={true}
              />
            </MobileStatusTagWrapper>
          </Flex>
        </RowLeft>

        <RowRight>
          <DesktopBalanceValue as="div">
            <BalanceWrapper>
              <Text type="secondary" style={{ fontSize: "inherit" }}>
                {t("balance")}
              </Text>
              {getBalanceValue()}
            </BalanceWrapper>
          </DesktopBalanceValue>
          <RowActions
            type={RowActionsType.ACCOUNT}
            account={account}
            activeAccountsInSlotCount={activeAccountsInSlotCount}
          />
        </RowRight>
      </AccountMainRow>
    </AccountRowWrapper>
  );
};

export default AccountRow;

const SMALL_MOBILE = "500px";
// Row padding (12) + half the name line (11).
const NEW_LOOK_BRANCH_TOP = 23;

const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;

  @media (max-width: ${LARGE_MOBILE}) {
    gap: 12px;
    flex: 1;
  }
`;
const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  flex: none;
  justify-content: flex-end;
  min-width: 0;
  padding-left: 8px;

  @media (max-width: ${SMALL_MOBILE}) {
    flex: none;
  }

  @media (max-width: ${LARGE_MOBILE}) {
    gap: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      gap: 20px;
    `}
`;
const AccountMainRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px 0;
  min-height: 60px;

  @media (max-width: ${LARGE_MOBILE}) {
    position: relative;
    align-items: center;
    gap: 6px;
    padding: 12px 0;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 12px 0;
      min-height: 0;
    `}
`;
const AccountExchangeName = styled(Text)`
  font-size: 13px;
  margin-top: 2px;

  @media (max-width: ${LARGE_MOBILE}) {
    font-size: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      font-size: 12px;
    `}
`;
const AccountRowWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding-right: 24px;
  background: transparent;
  margin-left: 52px;

  @media (max-width: ${LARGE_MOBILE}) {
    padding-right: 6px;
    margin-left: 0;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Account names share the user-name column (82px from the row edge;
         the slot panel is inset by 8). */
      && {
        padding-right: 0;
        margin-left: 74px;
        @media (max-width: 360px) {
          margin-left: 28px;
        }
      }
    `}
`;
const HierarchyLines = styled.div`
  position: absolute;
  left: -28px;
  top: 0;
  bottom: 0;
  width: 28px;
  background: transparent;
  z-index: 1;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Under the centre of the slot icon. */
      left: -29px;
    `}
`;
const VerticalLine = styled.div<{ $isFirst: boolean; $isLast: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: ${(p) => (p.$isLast ? "50%" : "0")};
  width: 1px;
  background: ${(p) => p.theme.colors.mediumBorderGrey};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      background: ${p.theme.colors.lightBorderGrey};
      /* The first line reaches up to the slot icon; the last one stops at
         its branch. */
      ${p.$isFirst &&
      css`
        top: -16px;
      `}
      ${p.$isLast &&
      css`
        bottom: calc(100% - ${NEW_LOOK_BRANCH_TOP + 1}px);
      `}
    `}
`;
const HorizontalLine = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  width: 16px;
  height: 1px;
  background: ${(p) => p.theme.colors.mediumBorderGrey};

  @media (max-width: ${LARGE_MOBILE}) {
    width: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Branch off at the account name, not the middle of the row. */
      top: ${NEW_LOOK_BRANCH_TOP}px;
      background: ${p.theme.colors.lightBorderGrey};
      && {
        width: 16px;
      }
    `}
`;
const AccountType = styled(Text)`
  font-size: 14px;
  font-weight: 500;

  @media (max-width: ${LARGE_MOBILE}) {
    font-size: 13px;
  }
`;
const DesktopBalanceValue = styled(Text)`
  font-size: 14px;

  @media (max-width: ${SMALL_MOBILE}) {
    display: none;
  }
`;
const MobileBalanceValue = styled(Text)`
  display: none;
  margin-top: 2px;

  @media (max-width: ${SMALL_MOBILE}) {
    display: block;
    ${(p) =>
      p.theme.isNewLook &&
      css`
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px 10px;
      `}
  }
`;
const DesktopStatusTagWrapper = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${SMALL_MOBILE}) {
    display: none;
  }
`;
const MobileStatusTagWrapper = styled.div`
  display: none;
  align-items: center;

  @media (max-width: ${SMALL_MOBILE}) {
    display: flex;
    ${(p) =>
      p.theme.isNewLook &&
      css`
        display: none;
      `}
  }
`;
const BalanceWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  @media (max-width: ${SMALL_MOBILE}) {
    font-size: 13px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      align-items: baseline;
    `}
`;
const BalanceText = styled(Text)`
  white-space: nowrap;
  margin-left: 6px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-left: 4px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      font-variant-numeric: tabular-nums;
    `}
`;
const NewLookAccountRow = styled.div`
  ${newLookTreeRow};
  min-height: 48px;
`;
const NewLookNames = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const NewLookName = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NewLookMeta = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const NewLookFigure = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
